import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faTrash, faSpinner } from '@fortawesome/free-solid-svg-icons';
import './DeleteConfirmationModal.css';

const DeleteConfirmationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title = "Delete Item", 
  message = "Are you sure you want to delete this item?",
  confirmText = "Delete",
  itemName = "",
  loading = false
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const modalRef = useRef(null);
  const overlayRef = useRef(null);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && !loading) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, loading]);

  // Handle close with animation
  const handleClose = () => {
    if (loading) return;
    
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  };

  // Handle click outside modal
  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current && !loading) {
      handleClose();
    }
  };

  // Handle confirm
  const handleConfirm = () => {
    if (!loading) {
      onConfirm();
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div 
      className={`modal-overlay ${isClosing ? 'closing' : ''}`}
      ref={overlayRef}
      onClick={handleOverlayClick}
    >
      <div 
        className={`delete-confirmation-modal ${isClosing ? 'closing' : ''}`}
        ref={modalRef}
      >
        {/* Header */}
        <div className="modal-header">
          <h2>{title}</h2>
          <button 
            className="close-button"
            onClick={handleClose}
            disabled={loading}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Content */}
        <div className="modal-content">
          <p className="delete-message">
            Are you sure you want to delete this item?
            {itemName && (
              <span className="item-name"> "{itemName}"</span>
            )}
          </p>
          
          <p className="warning-text">
            This action cannot be undone.
          </p>

          {/* Buttons */}
          <div className="modal-buttons">
            <button
              type="button"
              className="cancel-button"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="delete-button"
              onClick={handleConfirm}
              disabled={loading}
            >
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default DeleteConfirmationModal;